import { number } from "zod";
import { AccountStatuses } from "./AccountStatuses";
import { FirestoreDocAccount } from "./FirestoreDocAccount";
import dayjs, { Dayjs } from "dayjs";

/** Plaid's top-level account types (https://plaid.com/docs/api/accounts/). */
export const BREAD_ACCOUNT_TYPES = [
  "depository",
  "credit",
  "loan",
  "investment",
  "other",
] as const;
export type BreadAccountType = (typeof BREAD_ACCOUNT_TYPES)[number];

function parseAccountType(plaidType: string | undefined): BreadAccountType {
  // Plaid's legacy "brokerage" type is an investment account.
  if (plaidType === "brokerage") return "investment";
  return BREAD_ACCOUNT_TYPES.find((type) => type === plaidType) ?? "other";
}

/**
 * The computed type of an account, which is constructed from the raw
 * persisted data about an account which can contain information synced from
 * Plaid, data specified/overwritten by the user, etc.
 */
export type BreadAccount = {
  id: string;
  name: string;
  /** The name Plaid gave the account, before any rename by the user. */
  original_name: string;
  status: AccountStatuses;
  /** What kind of account it is, which decides how its balance counts. */
  type: BreadAccountType;
  /** Plaid's finer-grained kind, e.g. "checking", "cd", "mortgage", "401k". */
  subtype: string | null;
  mask: string | null;
  current_balance: number | null;
  institution_id: string | null;
  plaid_item_id: string | null;
  sync_start_date: Dayjs;
  order_position: number;
  currency_code: string;
  disappeared_from_plaid: boolean;
  closed_date: Dayjs | null;
};

/**
 * Parse the raw account data from the DB into a computed BreadTransaction.
 */
export const parseBreadAccount = (
  account: FirestoreDocAccount,
): BreadAccount | null => {
  let id = account.plaid_data?.account_id;
  let name: string | null | undefined = account.manual_data?.name;
  if (!name) {
    name = account.plaid_data?.name;
  }

  if (!id || !name) {
    return null;
  }

  // Plaid reports what's owed on credit and loan accounts as a positive
  // balance. Negate it so balances add up to net worth.
  const type = parseAccountType(account.plaid_data?.type);
  let balance = account.plaid_data?.balances.current ?? null;
  if (balance && (type === "credit" || type === "loan")) {
    balance = -balance;
  }

  const currencyCode =
    account.plaid_data?.balances?.iso_currency_code ??
    account.plaid_data?.balances?.unofficial_currency_code ??
    "USD";

  return {
    id: id,
    name: name,
    original_name: account.plaid_data?.name ?? name,
    status: account.system_data.status,
    type,
    subtype: account.plaid_data?.subtype ?? null,
    mask: account.plaid_data?.mask ?? null,
    current_balance: balance,
    institution_id: account.system_data.institution_id,
    plaid_item_id: account.system_data.plaid_item_id,
    sync_start_date: dayjs.unix(account.manual_data.sync_start_timestamp_secs),
    order_position: account.manual_data.order_position,
    currency_code: currencyCode,
    disappeared_from_plaid: account.system_data.disappeared_from_plaid ?? false,
    closed_date: account.system_data.closed_timestamp_secs
      ? dayjs.unix(account.system_data.closed_timestamp_secs)
      : null,
  };
};
